import { useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Iconify from '../../ui-component/iconify';
import TableStyle from '../../ui-component/TableStyle';
import { useState, useEffect } from 'react';
import { Stack, Button, Container, Typography, Box, Card, IconButton, Tooltip } from '@mui/material';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { toast } from 'react-toastify';
import { getApi, patchApi, postApi } from 'views/services/api';
import AddHotel from './AddHotel';
import EditHotel from './EditHotel';
import ChangePassword from './ChangePassword';
import DeleteHotel from './DeleteHotel';

// ----------------------------------------------------------------------

const HotelManagement = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [statusChanged, setStatusChanged] = useState(false);
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [openDelete, setOpenDelete] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [hotelData, sethotelData] = useState([]);
  const navigate = useNavigate();

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (hotel) => {
    setSelectedHotel(hotel);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedHotel(null);
    fetchhotelData(); // Refresh data after edit
  };

  const handleOpenChangePassword = (email) => {
    setSelectedEmail(email);
    setOpenChangePasswordModal(true);
  };

  const handleCloseChangePasswordModal = () => {
    setOpenChangePasswordModal(false);
    setSelectedEmail('');
  };

  const handleChangePassword = async (newPassword) => {
    try {
      const response = await patchApi('api/hotel/changehotelpassword', { email: selectedEmail, password: newPassword });
      if (response.status === 200) {
        toast.success('Contraseña cambiada exitosamente');
      } else {
        toast.error('Error al cambiar la contraseña');
      }
      handleCloseChangePasswordModal();
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Error al procesar el cambio de contraseña');
    }
  };

  const handleOpenChangeStatus = async (hotelId) => {
    try {
      const response = await patchApi(`api/hotel/changehotelstatus/${hotelId}`);
      if (response.status === 200) {
        toast.success('Estado actualizado correctamente');
        setStatusChanged((prevStatusChanged) => !prevStatusChanged);
      } else {
        toast.error('No se pudo cambiar el estado');
      }
    } catch (e) {
      console.log(e);
      toast.error('Error al intentar cambiar el estado');
    }
  };

  const handleOpenDelete = (hotel) => {
    setHotelToDelete(hotel);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setHotelToDelete(null);
  };

  const handleDeleteHotel = async (adminPassword) => {
    if (!hotelToDelete) return;
    setDeleting(true);
    try {
      const response = await postApi(`api/hotel/permanent-delete/${hotelToDelete._id}`, { adminPassword });
      if (response?.status === 200) {
        toast.success('Hotel y todos sus datos eliminados permanentemente');
        handleCloseDelete();
        fetchhotelData();
      } else {
        toast.error(response?.response?.data?.message || 'No se pudo eliminar el hotel');
      }
    } catch (error) {
      console.error('Error deleting hotel:', error);
      toast.error('Error al eliminar el hotel');
    } finally {
      setDeleting(false);
    }
  };

  const fetchhotelData = async () => {
    try {
      const response = await getApi('api/hotel/viewallhotels');
      sethotelData(response?.data || []);
    } catch (error) {
      console.error('Error fetching hotel data:', error);
    }
  };

  useEffect(() => {
    fetchhotelData();
  }, [statusChanged, openAdd, openChangePasswordModal]);

  const columns = [
    {
      field: 'name',
      headerName: 'Nombre del Hotel',
      flex: 1.5,
      cellClassName: 'name-column--cell name-column--cell--capitalize',
      renderCell: (params) => {
        const handleOpenView = () => {
          navigate(`/dashboard/hotel/view/${params.row._id}`);
        };
        return (
          <Typography 
            variant="subtitle1" 
            sx={{ 
              cursor: 'pointer', 
              color: '#1e88e5', 
              fontWeight: 600,
              '&:hover': { textDecoration: 'underline' }
            }} 
            onClick={handleOpenView}
          >
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'email',
      headerName: 'Email Administrador',
      flex: 1.2
    },
    {
      field: 'managerName',
      headerName: 'Encargado',
      flex: 1,
      valueGetter: (params) => params.row?.managerName || '—'
    },
    {
      field: 'managerPhone',
      headerName: 'Tel. Encargado',
      flex: 1,
      valueGetter: (params) => params.row?.managerPhone || '—'
    },
    {
      field: 'contact',
      headerName: 'Tel. Hotel',
      flex: 1,
      valueGetter: (params) => params.row?.contact || '—'
    },
    {
      field: 'country',
      headerName: 'País',
      flex: 0.9,
      valueGetter: (params) => params.row?.country || '—'
    },
    {
      field: 'deleted',
      headerName: 'Estado',
      flex: 0.8,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 700,
            backgroundColor: params?.value === false ? 'rgba(1, 181, 116, 0.12)' : 'rgba(255, 15, 15, 0.12)',
            color: params?.value === false ? '#01B574' : '#FF0F0F',
          }}
        >
          {params?.value === false ? 'Activo' : 'Inactivo'}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 1.8,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Editar Hotel">
            <IconButton 
              size="small" 
              onClick={() => handleOpenEdit(params.row)}
              sx={{ color: '#FF9800', backgroundColor: 'rgba(255, 152, 0, 0.12)', '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.25)' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Cambiar Contraseña">
            <IconButton 
              size="small" 
              onClick={() => handleOpenChangePassword(params.row.email)}
              sx={{ color: '#2196F3', backgroundColor: 'rgba(33, 150, 243, 0.12)', '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.25)' } }}
            >
              <ChangeCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={params.row.deleted ? "Activar Hotel" : "Desactivar Hotel"}>
            <IconButton 
              size="small" 
              onClick={() => handleOpenChangeStatus(params.row._id)}
              sx={{ 
                color: params.row.deleted ? '#01B574' : '#FF0F0F', 
                backgroundColor: params.row.deleted ? 'rgba(1, 181, 116, 0.12)' : 'rgba(255, 15, 15, 0.12)',
                '&:hover': { 
                  backgroundColor: params.row.deleted ? 'rgba(1, 181, 116, 0.25)' : 'rgba(255, 15, 15, 0.25)' 
                } 
              }}
            >
              <Iconify icon={params.row.deleted ? "eva:checkmark-circle-2-fill" : "eva:slash-fill"} width={20} />
            </IconButton>
          </Tooltip>

          {params.row.role !== 'admin' && (
            <Tooltip title="Eliminar Hotel">
              <IconButton
                size="small"
                onClick={() => handleOpenDelete(params.row)}
                sx={{ color: '#D32F2F', backgroundColor: 'rgba(211, 47, 47, 0.12)', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.25)' } }}
              >
                <DeleteForeverIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )
    }
  ];

  return (
    <>
      <AddHotel open={openAdd} handleClose={handleCloseAdd} />
      {selectedHotel && (
        <EditHotel 
          open={openEdit} 
          handleClose={handleCloseEdit} 
          data={selectedHotel} 
        />
      )}
      <ChangePassword
        open={openChangePasswordModal}
        handleClose={handleCloseChangePasswordModal}
        email={selectedEmail}
        onChangePassword={handleChangePassword}
      />
      <DeleteHotel
        open={openDelete}
        handleClose={handleCloseDelete}
        hotel={hotelToDelete}
        onDelete={handleDeleteHotel}
        loading={deleting}
      />
      
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" mb={5} justifyContent={'space-between'}>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>Gestión de Hoteles</Typography>
          <Button 
            variant="contained" 
            startIcon={<Iconify icon="eva:plus-fill" />} 
            onClick={handleOpenAdd}
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            Nuevo Hotel
          </Button>
        </Stack>

        <TableStyle>
          <Box width="100%">
            <Card sx={{ 
              height: '650px', 
              p: 2, 
              borderRadius: '16px',
              boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)'
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4" sx={{ ml: 1 }}>
                  Hoteles Registrados <Typography component="span" variant="h4" color="textSecondary">({hotelData?.length || 0})</Typography>
                </Typography>
              </Stack>
              
              {hotelData && (
                <DataGrid
                  rows={hotelData}
                  columns={columns}
                  checkboxSelection
                  getRowId={(row) => row?._id}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{ 
                    toolbar: { 
                      showQuickFilter: true,
                      sx: { pb: 2 }
                    } 
                  }}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f4f6f8',
                      borderRadius: '8px',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none'
                    }
                  }}
                />
              )}
            </Card>
          </Box>
        </TableStyle>
      </Container>
    </>
  );
};

export default HotelManagement;
